/**
 * Design Tokens - Single Source of Truth for Visual Design
 * 
 * This file contains all design tokens for the application.
 * NEVER hardcode values in components - always use these tokens.
 * 
 * Usage:
 * import { colors, spacing, typography } from '../styles/designTokens';
 * 
 * Example:
 * style={{ color: colors.primary.main, padding: spacing.md }}
 */

// Color System
export const colors = {
  // Primary brand color (blue)
  primary: {
    main: '#2563EB',
    light: '#3B82F6',
    dark: '#1D4ED8',
    50: '#EFF6FF',
    100: '#DBEAFE',
    500: '#3B82F6',
    600: '#2563EB',
    700: '#1D4ED8'
  },
  
  // Success states (green)
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
    50: '#ECFDF5',
    100: '#D1FAE5',
    500: '#10B981',
    600: '#059669',
    700: '#047857'
  },
  
  // Error states (red)
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
    50: '#FEF2F2',
    100: '#FEE2E2',
    500: '#EF4444',
    600: '#DC2626',
    700: '#B91C1C'
  },
  
  // Warning states (yellow/orange)
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    50: '#FFFBEB',
    100: '#FEF3C7',
    500: '#F59E0B',
    600: '#D97706',
    700: '#B45309'
  },
  
  // Info states (cyan)
  info: {
    main: '#06B6D4',
    light: '#22D3EE',
    dark: '#0891B2',
    50: '#ECFEFF',
    100: '#CFFAFE',
    500: '#06B6D4',
    600: '#0891B2',
    700: '#0E7490'
  },
  
  // Neutral grays
  neutral: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827'
  },
  
  // Leaderboard rank colors
  rank: {
    gold: {
      bg: '#FFFBEB',      // bg-amber-50
      border: '#FBBF24',   // border-amber-400
      text: '#B45309'      // text-amber-700
    },
    silver: {
      bg: '#F9FAFB',       // bg-gray-50
      border: '#9CA3AF',   // border-gray-400
      text: '#374151'      // text-gray-700
    },
    bronze: {
      bg: '#FFF7ED',       // bg-orange-50
      border: '#FB923C',   // border-orange-400
      text: '#C2410C'      // text-orange-700
    },
    currentUser: {
      bg: '#EFF6FF',       // bg-blue-50
      border: '#3B82F6',   // border-blue-500
      text: '#1D4ED8'      // text-blue-700
    }
  }
};

// Spacing Scale (rem-based for accessibility)
export const spacing = {
  xs: '0.25rem',    // 4px
  sm: '0.5rem',     // 8px
  md: '1rem',       // 16px
  lg: '1.5rem',     // 24px
  xl: '2rem',       // 32px
  '2xl': '3rem',    // 48px
  '3xl': '4rem',    // 64px
  '4xl': '6rem'     // 96px
};

// Typography Scale
export const typography = {
  h1: {
    size: '2rem',        // 32px
    weight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em'
  },
  h2: {
    size: '1.5rem',      // 24px
    weight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em'
  },
  h3: {
    size: '1.25rem',     // 20px
    weight: 600,
    lineHeight: 1.4,
    letterSpacing: 'normal'
  },
  h4: {
    size: '1.125rem',    // 18px
    weight: 600,
    lineHeight: 1.4,
    letterSpacing: 'normal'
  },
  body: {
    size: '1rem',        // 16px
    weight: 400,
    lineHeight: 1.5,
    letterSpacing: 'normal'
  },
  bodyLarge: {
    size: '1.125rem',    // 18px
    weight: 400,
    lineHeight: 1.5,
    letterSpacing: 'normal'
  },
  small: {
    size: '0.875rem',    // 14px
    weight: 400,
    lineHeight: 1.4,
    letterSpacing: 'normal'
  },
  xs: {
    size: '0.75rem',     // 12px
    weight: 400,
    lineHeight: 1.4,
    letterSpacing: 'normal'
  },
  // Special typography for metrics/numbers
  metric: {
    primary: {
      size: '3rem',      // 48px - For primary leaderboard metric
      weight: 700,
      lineHeight: 1,
      letterSpacing: '-0.02em'
    },
    secondary: {
      size: '1.5rem',    // 24px - For secondary metrics
      weight: 600,
      lineHeight: 1.2,
      letterSpacing: 'normal'
    }
  }
};

// Font Families
export const fontFamily = {
  sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono: '"SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
};

// Border Radius
export const borderRadius = {
  none: '0',
  sm: '0.125rem',    // 2px
  md: '0.375rem',    // 6px
  lg: '0.5rem',      // 8px
  xl: '0.75rem',     // 12px
  '2xl': '1rem',     // 16px
  full: '9999px'
};

// Shadows
export const shadows = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)'
};

// Transitions
export const transitions = {
  fast: '100ms cubic-bezier(0.4, 0, 0.2, 1)',
  normal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
  slowest: '500ms cubic-bezier(0.4, 0, 0.2, 1)'
};

// Z-Index Scale
export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  fixed: 1200,
  modalBackdrop: 1300,
  modal: 1400,
  popover: 1500,
  tooltip: 1600,
  toast: 1700
};

// Breakpoints (for responsive design - desktop-first)
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px'
};

// Icon Sizes
export const iconSizes = {
  xs: '0.75rem',     // 12px
  sm: '1rem',        // 16px
  md: '1.25rem',     // 20px
  lg: '1.5rem',      // 24px
  xl: '2rem',        // 32px
  '2xl': '3rem'      // 48px
};

// Helper function to generate Tailwind-style classes from tokens
export const getTailwindClass = {
  color: (color) => {
    // Helper to convert token to Tailwind class
    // Usage: getTailwindClass.color(colors.primary.main) => 'text-blue-600'
    const colorMap = {
      [colors.primary.main]: 'text-blue-600',
      [colors.success.main]: 'text-green-500',
      [colors.error.main]: 'text-red-500',
      [colors.warning.main]: 'text-yellow-500',
    };
    return colorMap[color] || 'text-gray-900';
  }
};

export default {
  colors,
  spacing,
  typography,
  fontFamily,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  breakpoints,
  iconSizes
};

